/** Live/Paper view state that opens on — and persists to — the shared
 * remembered execution-mode preference (same one the Trade Log uses).
 * During a paper-test phase a hard LIVE default made the Reports pages
 * look empty every visit (2026-07-13). */

import { useEffect, useRef, useState } from "react";
import type { ReportExecutionMode } from "@/lib/reportsApi";
import {
  getSavedExecutionMode,
  saveExecutionMode,
  subscribeExecutionMode,
} from "@/lib/tradeLogPrefs";

/** Follow a Live/Paper switch made in ANOTHER browser tab.
 *
 * The report pages open in their own tab, so a report and the Trade Log are
 * routinely on screen together reading one preference. Without this they
 * disagree until one is reloaded, and two views of the same book showing
 * different numbers reads as a data bug. */
export function useExecutionModeFromOtherTabs(onChange: (mode: ReportExecutionMode) => void): void {
  const latest = useRef(onChange);
  latest.current = onChange;
  useEffect(
    () =>
      subscribeExecutionMode((m) => {
        latest.current(m);
      }),
    [],
  );
}

export function useRememberedExecutionMode(): [
  ReportExecutionMode,
  (m: ReportExecutionMode) => void,
] {
  const [mode, setModeState] = useState<ReportExecutionMode>(getSavedExecutionMode);
  useExecutionModeFromOtherTabs(setModeState);
  const setMode = (m: ReportExecutionMode): void => {
    saveExecutionMode(m);
    setModeState(m);
  };
  return [mode, setMode];
}
